import posthog from 'posthog-js';
import { snakeCase } from 'es-toolkit/compat';
import { BaseMapLayer } from 'state/useMapLayersState';
import chroma, { InterpolationMode } from 'chroma-js';
import { Theme } from '@mui/material';
import {
  Map,
  LayerProps,
  LineLayerSpecification,
  ExpressionSpecification,
  MapRef,
  LngLatBounds,
  MapEventOf,
} from 'shared/map-exports';
import { Capacitor } from '@capacitor/core';
import { CSSObject } from 'tss-react';
import {
  MapDrawerEntityType,
  Membership,
  LatLng,
  ResolvedUserPermissions,
  UserPermissionKey,
} from 'shared/types';
import { isIframe } from 'shared/utils';
import { MAP_ENTITY_DRAWER_WIDTH } from 'features/mapping/components/MapEntityDrawer';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';
import centroid from '@turf/centroid';
import { Polygon, MultiPolygon } from 'geojson';
import { DARK_SURFACE } from 'theme/palette';
import {
  ColorStop,
  ExternalFeatureLayerServer,
  LayerGroup,
  PoiType,
} from './mapping.types';
import {
  ExternalFeatureLayerName,
  LAYER_GROUPS,
  MapLayer,
  MapLayerDetail,
  MapViewDimension,
  MapLayers,
  SELECTED_FEATURE_HIGHLIGHT_COLOR,
  UNGATED_HOTSPOT_LAYERS,
} from './mapping.constants';
import {
  getSearchCardMinSnapPx,
  SEARCH_DRAWER_WIDTH,
  STATUS_ALERT_OFFSET_CSS_VALUE,
} from '../../constants';

export type StyleFunctionProps = {
  idKey: string | ExpressionSpecification;
  otherLayerProps?: Partial<LayerProps>;
  selectedId: number | string;
  patternIconName?: string | ExpressionSpecification;
};

export type MapLayerAccessContext = {
  showMembershipFeatures: boolean;
  showMembershipProFeatures: boolean;
  permissions?: ResolvedUserPermissions;
};

export type CanUserAccessMapLayerArgs = MapLayerAccessContext & {
  minMembership?: Membership;
  requiredPermissions?: UserPermissionKey[];
};

// The satellite-hotspots group lists Landsat and NGFS alongside MODIS/VIIRS, but
// each is flag-gated independently: Landsat only with the GIS heat-detections
// flag, NGFS only with its own flag. Filter out whichever flag is off so the
// layer isn't shown or counted in the layers drawer.
export const getVisibleGroupLayers = (
  layerGroup: LayerGroup,
  isHeatDetectionsGisEnabled: boolean,
  isNgfsDetectionsEnabled: boolean,
): readonly MapLayer[] => {
  const layers = LAYER_GROUPS[layerGroup];
  if (layerGroup !== 'satelliteHotspots') {
    return layers;
  }
  return layers.filter((layer) => {
    if (layer === MapLayers.NGFS_DETECTIONS) {
      return isNgfsDetectionsEnabled;
    }
    if (!isHeatDetectionsGisEnabled) {
      return UNGATED_HOTSPOT_LAYERS.includes(layer);
    }
    return true;
  });
};

const isWebGL2 = (
  context: WebGLRenderingContext | WebGL2RenderingContext,
): context is WebGL2RenderingContext => {
  return (
    'WebGL2RenderingContext' in window &&
    context instanceof WebGL2RenderingContext
  );
};

export const hasWebGL2Support = (map: Map): boolean => {
  if (!map.painter) return false;
  const {
    painter: {
      context: { gl },
    },
  } = map;
  try {
    if (!gl.getParameter(gl.VERSION).includes('WebGL 2.0')) return false;

    if (!isWebGL2(gl)) return false;

    // Check for specific WebGL 2.0 features
    const features = [
      // Transform Feedback
      'createTransformFeedback' in gl && 'bindTransformFeedback' in gl,
      // Vertex Array Objects
      'createVertexArray' in gl && 'bindVertexArray' in gl,
      // Instanced rendering
      'drawArraysInstanced' in gl && 'drawElementsInstanced' in gl,
      // Uniform Buffer Objects
      'createBuffer' in gl && 'bindBufferBase' in gl,
      // Check for some WebGL 2.0 specific constants
      'TRANSFORM_FEEDBACK_BUFFER' in gl,
      'UNIFORM_BUFFER' in gl,
    ];

    // Check if all features are supported
    return features.every((feature) => feature === true);
  } catch (e) {
    return false;
  }
};

export const SELECTED_POLYGON_FILL_PATTERN = 'SelectedPolygonPattern';

export const SELECTED_POLYGON_STROKE_PAINT: LineLayerSpecification['paint'] = {
  'line-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
  'line-opacity': 1,
  'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.25, 18, 3.25],
};

export const getSelectedPatternStyle = (
  props: StyleFunctionProps,
): LayerProps => {
  const {
    idKey,
    otherLayerProps = {},
    selectedId,
    patternIconName = SELECTED_POLYGON_FILL_PATTERN,
  } = props;

  return {
    ...otherLayerProps,
    type: 'fill',
    paint: {
      'fill-pattern': patternIconName,
      ...(otherLayerProps.paint ? otherLayerProps.paint : {}),
    },
    filter: ['==', idKey, selectedId],
  };
};

export const getSelectedStrokeStyle = (
  props: StyleFunctionProps,
): LayerProps => {
  const { idKey, otherLayerProps = {}, selectedId } = props;
  return {
    ...otherLayerProps,
    type: 'line',
    paint: {
      ...SELECTED_POLYGON_STROKE_PAINT,
      ...(otherLayerProps.paint ? otherLayerProps.paint : {}),
    },
    filter: ['==', idKey, selectedId],
  };
};

/** Fixed dark shell for map bottom overlays (radar, fire progression model). */
const getMapOverlayDialogPaperStyles = (theme: Theme): CSSObject => {
  const { warmGrey } = theme.palette;
  const backgroundColor = DARK_SURFACE;
  const color = warmGrey[50];

  return {
    backgroundColor,
    // MUI Paper elevation adds a palette-dependent `background-image` overlay.
    backgroundImage: 'none',
    color,
    '& .MuiTypography-root': { color: 'inherit' },
    '& .MuiSvgIcon-root': { color: 'inherit' },
  };
};

export const getMapDialogCommonStyles = (
  theme: Theme,
  options: {
    isDrawerEntitySelected: boolean;
    isSearchDrawerOpen: boolean;
    minWidth: number;
    minSnapPct: number;
    baseYOffset?: number;
    /** e.g. 'var(--drawer-height, 0px)' */
    baseYOffsetVar?: string;
    extraYOffset?: number;
    /**
     * Fixed dark paper in light or dark app mode (radar, FPM).
     * Wrap dialog children in `ForcedTheme mode="dark"` when true.
     */
    isDarkShell?: boolean;
    /**
     * Folds `STATUS_ALERT_OFFSET_CSS_VALUE` into the lift transform to clear
     * `PersistentStatusAlert`.
     */
    trackStatusAlertOffset?: boolean;
  },
): Record<'root' | 'container' | 'paper', CSSObject> => {
  const {
    isDrawerEntitySelected,
    isSearchDrawerOpen,
    minWidth,
    minSnapPct,
    baseYOffset = Math.round(window.innerHeight * minSnapPct),
    baseYOffsetVar,
    extraYOffset = 0,
    isDarkShell = false,
    trackStatusAlertOffset = false,
  } = options;

  const desktopOffset = isSearchDrawerOpen
    ? MAP_ENTITY_DRAWER_WIDTH - SEARCH_DRAWER_WIDTH
    : MAP_ENTITY_DRAWER_WIDTH;

  const searchBottomOffset = getSearchCardMinSnapPx();
  // Only used when baseYOffsetVar is not provided
  const mobileTranslateYOffset =
    baseYOffset + extraYOffset - searchBottomOffset;

  // Platform-specific vertical offset compensation in pixels for dialog positioning
  // Values determined through empirical testing:
  // Native platforms (iOS/Android): -8px offset needed to align modal
  // Web platforms: -24px offset needed to align modal
  const mobilePlatformOffset = Capacitor.isNativePlatform() ? -8 : -24;

  // Half the offset: visually tuned, `getMarginBottom` covers the rest.
  const statusAlertOffset = trackStatusAlertOffset
    ? `calc(${STATUS_ALERT_OFFSET_CSS_VALUE} / 2)`
    : '0px';

  const withStatusAlertOffset = (translateYPx: number): string => {
    if (!trackStatusAlertOffset) return `translateY(-${translateYPx}px)`;
    return `translateY(calc(-1 * (${translateYPx}px + ${statusAlertOffset})))`;
  };

  const getMobileTransform = (): string => {
    if (isDrawerEntitySelected) {
      if (baseYOffsetVar) {
        // static parts pre-computed, only baseYOffset is dynamic
        const staticOffset = extraYOffset - searchBottomOffset;
        if (trackStatusAlertOffset) {
          return `translateY(calc(-1 * (${baseYOffsetVar} + ${staticOffset}px + ${statusAlertOffset})))`;
        }
        return `translateY(calc(-1 * (${baseYOffsetVar} + ${staticOffset}px)))`;
      }
      return withStatusAlertOffset(mobileTranslateYOffset);
    }
    if (extraYOffset || trackStatusAlertOffset) {
      return withStatusAlertOffset(extraYOffset);
    }
    return `translateY(0)`;
  };

  const getMarginBottom = (desktop: boolean): string | number => {
    if (Capacitor.isNativePlatform()) {
      return 'calc(30px + env(safe-area-inset-bottom))';
    }
    if (isIframe()) {
      if (desktop) return 96;
      if (isDrawerEntitySelected) return 48;
      return 118;
    }
    return 48;
  };

  return {
    root: {
      position: 'absolute !important' as 'absolute',
      width: '100%',
      inset: 'unset !important',
      left: '0px !important',
      bottom: '0px !important',
      pointerEvents: 'none',
      [theme.breakpoints.up('laptop')]: {
        transform: (() => {
          if (isDrawerEntitySelected) {
            return `translateX(-${desktopOffset / 2}px) ${withStatusAlertOffset(extraYOffset)}`;
          }
          if (extraYOffset || trackStatusAlertOffset) {
            return withStatusAlertOffset(extraYOffset);
          }
          return 'translateX(0)';
        })(),
        transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      },
      [theme.breakpoints.between('tablet', 'laptop')]: {
        position: isDrawerEntitySelected
          ? ('fixed !important' as 'fixed')
          : undefined,
        transform:
          extraYOffset || trackStatusAlertOffset
            ? withStatusAlertOffset(extraYOffset)
            : `translateY(0)`,
        transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      },
      [theme.breakpoints.down('tablet')]: {
        bottom: `calc(${searchBottomOffset + mobilePlatformOffset}px - env(safe-area-inset-bottom)) !important`,
        transform: getMobileTransform(),
        transition: 'transform 1s cubic-bezier(0, 0, 0.2, 1) 0ms',
      },
    },
    container: {
      width: '100%',
      [theme.breakpoints.down(480)]: {
        minWidth: 'unset',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        width: 'calc(100% - 32px)', // 16px margin on each side
      },
      position: 'relative',
    },
    paper: {
      margin: theme.spacing(0, 2),
      width: 'fit-content',
      maxHeight: 'max-content',
      minWidth,
      borderRadius: 8,
      display: 'inline-block',
      position: 'absolute',
      pointerEvents: 'auto',
      bottom: getMarginBottom(true),
      [theme.breakpoints.down('tablet')]: {
        bottom: getMarginBottom(false),
      },
      [theme.breakpoints.down(480)]: {
        width: '100%',
        minWidth: 'unset',
      },
      ...(isDarkShell ? getMapOverlayDialogPaperStyles(theme) : {}),
    },
  };
};

export const layerNameToPoiType: Record<string, PoiType> = {
  FlightTracker: 'aircraft',
  PowerOutages: 'powerOutage',
  EvacuationZones: 'evacZone',
  ExternalGeoEvents: 'externalGeoEvent',
  ResponsibilityAreas: 'responsibilityArea',
  ElectricalLines: 'electricalLine',
  GasPipelines: 'gasPipeline',
  FemaFloodHazard: 'femaFloodHazard',
  Lightning: 'lightning',
};

export const layerNameToEntityType: Record<string, MapDrawerEntityType> = {
  [MapLayers.PRIVATE_LAND_OWNERSHIP]: 'privateLandOwnership',
};

/**
 * Returns the current map zoom level as an integer.
 * Uses a custom rounding logic where values from 0.75 and up round to the next integer,
 * while values below 0.75 round down to the current integer.
 */
export const getMapZoomInteger = (map?: MapRef): number | undefined => {
  if (!map) return undefined;
  return Math.floor(map.getZoom() + 0.25);
};

/**
 * Returns the current map's viewport bounds as a LngLatBounds object with integer values.
 * The bounds are rounded to the nearest integer in such a way that the returned bounding
 * box always will contain the map's current viewport.
 */
export const getMapBoundsInteger = (map?: MapRef): LngLatBounds | undefined => {
  if (!map) return undefined;
  const bounds = map.getBounds();
  if (!bounds) return undefined;
  return new LngLatBounds([
    Math.floor(bounds.getWest()),
    Math.floor(bounds.getSouth()),
    Math.ceil(bounds.getEast()),
    Math.ceil(bounds.getNorth()),
  ]);
};

/**
 * Whether the user can access a layer. Holding every entry in
 * `requiredPermissions` grants access on its own, regardless of membership.
 *
 * User permissions are gated on the backend by a release flag, so when they
 * aren't granted we fall back to `minMembership` for backwards compatibility.
 */
export const canUserAccessMapLayer = (
  input: CanUserAccessMapLayerArgs,
): boolean => {
  const {
    minMembership,
    showMembershipFeatures,
    showMembershipProFeatures,
    requiredPermissions,
    permissions,
  } = input;

  if (requiredPermissions?.length) {
    if (requiredPermissions.every((permission) => permissions?.[permission])) {
      return true;
    }
    // `MapLayerDetail` rules this out; guard anyway so a hand-built gate with
    // permissions but no tier fails closed instead of becoming public.
    if (!minMembership) return false;
  }

  if (minMembership === 'regular') {
    return showMembershipFeatures;
  }

  if (minMembership === 'pro') {
    return showMembershipProFeatures;
  }

  return true;
};

/**
 * Returns the paywall URL to redirect to if `details` gates `mapLayer` behind a
 * membership tier the user doesn't have, or `null` if there's no paywall to
 * show. `MapLayerDetail` requires a `minMembership` alongside
 * `requiredPermissions`, so every denial maps to a tier and `null` means the
 * layer is accessible.
 */
export const getMapLayerPaywallLink = (
  mapLayer: MapLayer,
  details: MapLayerDetail,
  access: MapLayerAccessContext,
): string | null => {
  const hasAccess = canUserAccessMapLayer({
    minMembership: details.minMembership,
    requiredPermissions: details.requiredPermissions,
    ...access,
  });

  if (hasAccess) return null;

  const source = `map_layer:${snakeCase(mapLayer)}`;

  if (details.minMembership === 'pro') {
    return getPaywallLink({ focusedPlan: MEMBERSHIP_PLANS.proYearly, source })
      .url;
  }

  if (details.minMembership === 'regular') {
    return getPaywallLink({ focusedPlan: MEMBERSHIP_PLANS.yearly, source }).url;
  }

  return null;
};

export const parseEvacZoneName = (zoneName: string): string => {
  return zoneName.replace(/^\s*\bzone\b\s*/i, '');
};

export const getDirectionFromBearing = (deg: number): string => {
  // Convert to [-180, 180] to support both Mapbox bearings
  // and standard 0-360 wind direction
  const wrapped = ((deg % 360) + 360) % 360;
  const bearing = wrapped < 180 ? wrapped : wrapped - 360;

  const N = 0;
  const NE = 45;
  const E = 90;
  const SE = 135;
  const SW = -135;
  const W = -90;
  const NW = -45;
  const half = 45 / 2;

  if (bearing > N + half && bearing < NE + half) {
    return 'NE';
  }
  if (bearing >= NE + half && bearing < E + half) {
    return 'E';
  }
  if (bearing >= E + half && bearing < SE + half) {
    return 'SE';
  }
  if (bearing >= SE + half) {
    return 'S';
  }
  if (bearing <= N - half && bearing > NW - half) {
    return 'NW';
  }
  if (bearing <= NW - half && bearing > W - half) {
    return 'W';
  }
  if (bearing <= W - half && bearing > SW - half) {
    return 'SW';
  }
  if (bearing <= SW - half) {
    return 'S';
  }

  return 'N'; // default
};

export const getExternalFeatureLayerId = (
  featureLayer: ExternalFeatureLayerServer,
): ExternalFeatureLayerName => {
  return `organization_${featureLayer.organizationId}_${featureLayer.shortName}` as ExternalFeatureLayerName;
};

export const trackMapLayerToggleToPosthog = (
  layerName: string,
  isToggleOn: boolean,
): void => {
  const eventName = isToggleOn
    ? 'toggled_map_layer_on'
    : 'toggled_map_layer_off';

  posthog.capture(eventName, {
    layerName,
  });
};

export const trackMapBaseLayerChangeToPosthog = (
  baseLayer: BaseMapLayer,
): void => {
  posthog.capture('changed_map_base_layer', {
    baseLayer,
  });
};

export const trackMapDimensionChangeToPosthog = (
  dimension: MapViewDimension,
): void => {
  posthog.capture('changed_map_dimension', {
    dimension,
  });
};

export const getInterpolatedColor = (
  value: number,
  stops: ColorStop[],
  mode: InterpolationMode = 'rgb',
): string => {
  const domain = stops.map((s) => s.value);
  const range = stops.map((s) => s.color);
  const scale = chroma.scale(range).domain(domain).mode(mode);
  return scale(value).hex();
};

/**
 * Returns the centroid of a GeoJSON polygon geometry as a `LatLng`,
 * or `null` if the centroid cannot be computed.
 */
export const getCentroid = (geom: Polygon | MultiPolygon): LatLng | null => {
  try {
    const center = centroid(geom);
    return {
      lat: center.geometry.coordinates[1],
      lng: center.geometry.coordinates[0],
    };
  } catch {
    return null;
  }
};

// Mapbox fires `movestart` with a DOM `originalEvent` for window `resize` and
// `orientationchange`. These are layout changes, not user gestures.
const LAYOUT_MOVE_EVENT_TYPES = new Set(['resize', 'orientationchange']);

/**
 * Whether a move event came from a user gesture. Programmatic moves (`easeTo`,
 * `resize`) carry no `originalEvent`; layout-driven ones report a DOM `resize`
 * or `orientationchange`.
 */
export const isUserInitiatedMove = (
  event: MapEventOf<'movestart'>,
): boolean => {
  const { originalEvent } = event;
  if (!originalEvent) {
    return false;
  }
  return !LAYOUT_MOVE_EVENT_TYPES.has(originalEvent.type);
};
